import React, { useState, useEffect } from 'react';
import { Plus, Copy, Ban, RefreshCw, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';

const EMPTY_FORM = { percent_off: '', max_redemptions: '', expires_at: '', label: '' };

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listPromoCodes();
      setPromoCodes(res.data.promo_codes || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const percentOff = parseFloat(form.percent_off);
    if (!percentOff || percentOff <= 0 || percentOff > 100) {
      toast.error('Le pourcentage doit être compris entre 0 et 100');
      return;
    }
    if (form.max_redemptions && parseInt(form.max_redemptions, 10) < 1) {
      toast.error('Le nombre max de rédemptions doit être supérieur ou égal à 1');
      return;
    }
    if (form.expires_at && new Date(form.expires_at) <= new Date()) {
      toast.error("La date d'expiration doit être dans le futur");
      return;
    }

    setCreating(true);
    try {
      const res = await adminAPI.createPromoCode({
        percent_off: percentOff,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        label: form.label || null,
      });
      toast.success(`Code créé : ${res.data.code}`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du code');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Désactiver ce code promo ?')) return;
    try {
      await adminAPI.deactivatePromoCode(id);
      toast.success('Code promo désactivé');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Codes promo marketplace</h4>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} className="mr-1" />
          Nouveau code
        </Button>
      </div>

      {showForm && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <Input
              type="number"
              placeholder="% de réduction"
              value={form.percent_off}
              onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Max rédemptions (optionnel)"
              value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
            />
            <Input
              type="date"
              placeholder="Expiration (optionnel)"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            />
          </div>
          <Input
            placeholder="Note interne (optionnel, ex: Lancement Q1)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Création...' : 'Créer le code'}
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {promoCodes.map((promo) => (
          <div key={promo.id} className="flex items-center justify-between bg-white border rounded-lg p-4">
            <div>
              <div className="flex items-center gap-2">
                <Tag size={14} className="text-blue-900" />
                <span className="font-mono font-medium text-gray-800">{promo.code}</span>
                <button
                  onClick={() => handleCopy(promo.code)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Copier"
                >
                  <Copy size={14} />
                </button>
                {promo.is_active ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Actif</span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Inactif</span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                -{promo.percent_off}% · {promo.times_redeemed} / {promo.max_redemptions ?? 'illimité'} utilisations
                {promo.expires_at
                  ? ` · expire le ${new Date(promo.expires_at).toLocaleDateString('fr-FR')}`
                  : ' · sans expiration'}
              </p>
              {promo.label && <p className="text-xs text-gray-400 mt-1">{promo.label}</p>}
            </div>
            {promo.is_active && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeactivate(promo.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Ban size={14} className="mr-1" />
                Désactiver
              </Button>
            )}
          </div>
        ))}
        {promoCodes.length === 0 && !loading && (
          <p className="text-center text-gray-500 py-4">Aucun code promo créé</p>
        )}
      </div>
    </div>
  );
};

export default AdminPromoCodes;
